import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Module-level flag: resets on every page load (and when visiting a public
// page), so the prompt re-appears each time the user enters the dashboard,
// but not when navigating between dashboard tabs.
let shownThisVisit = false;

/** Called from public-site pages so the prompt shows again on next dashboard entry. */
export const resetVerificationPrompt = () => {
  shownThisVisit = false;
};

interface VerificationPromptModalProps {
  role: "buyer" | "seller";
}

const VerificationPromptModal = ({ role }: VerificationPromptModalProps) => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    // "pending" is the default for users who never started verification.
    // Suppress only for verified users and submissions awaiting admin review.
    if (profile.kyc_status === "verified" || profile.kyc_status === "pending_admin_review") return;
    if (shownThisVisit) return;
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [profile]);

  const dismiss = () => {
    shownThisVisit = true;
    setOpen(false);
  };

  const handleVerify = () => {
    shownThisVisit = true;
    setOpen(false);
    navigate("/kyc");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="bg-card border-border max-w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-foreground">Get Verified (Optional)</DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {role === "buyer"
              ? "Verify your identity to display an \"Identity Verified\" badge on your domain requests. Sellers trust verified buyers more — it's quick and completely optional."
              : "Verify your identity to display an \"Identity Verified\" badge on your proposals. Buyers trust verified sellers more — it's quick and completely optional."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <BadgeCheck className="h-5 w-5 shrink-0 text-primary" />
          <p className="text-xs text-muted-foreground">
            {role === "buyer"
              ? "Your requests will show an Identity Verified badge instead of \"Not Verified\"."
              : "Your proposals will show an Identity Verified badge instead of \"Not Verified\". Note: verification is required later only when requesting a payout."}
          </p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
          <Button variant="ghost" onClick={dismiss} className="text-muted-foreground">
            Maybe Later
          </Button>
          <Button onClick={handleVerify} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Verify My Identity
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationPromptModal;